import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";

// Near-realtime in-app pop-ups via WebSocket (with polling fallback).
export default function LiveNotifier() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const seen = useRef(new Set());
  const inited = useRef(false);
  const wsRef = useRef(null);

  useEffect(() => {
    if (!user) { inited.current = false; seen.current = new Set(); if (wsRef.current) wsRef.current.close(); return; }

    const showNotif = (n) => {
      if (!n || !n.id || seen.current.has(n.id)) return;
      seen.current.add(n.id);
      toast(n.title, { description: n.body, action: n.link ? { label: "View", onClick: () => navigate(n.link) } : undefined });
    };

    // Prime seen-set with existing, then start polling fallback
    const poll = async () => {
      try {
        const r = await api.get("/notifications");
        const list = r.data || [];
        if (!inited.current) { list.forEach((n) => seen.current.add(n.id)); inited.current = true; return; }
        list.slice().reverse().forEach(showNotif);
      } catch (e) { /* ignore */ }
    };
    poll();
    const pt = setInterval(poll, 12000);

    // WebSocket for zero-delay push
    let stop = false;
    let reconnectT;
    const connect = () => {
      const token = localStorage.getItem("ca_token");
      if (!token) return;
      const wsUrl = `${API.replace(/^http/, "ws")}/ws?token=${token}`;
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.onmessage = (ev) => {
          try {
            const data = JSON.parse(ev.data);
            if (data.event === "notification") showNotif(data);
            else if (data.event === "message") window.dispatchEvent(new CustomEvent("ca:new-message", { detail: { complaintId: data.complaint_id } }));
          } catch (e) { /* ignore */ }
        };
        ws.onclose = () => { if (!stop) reconnectT = setTimeout(connect, 3000); };
        ws.onerror = () => { try { ws.close(); } catch (e) { /* ignore */ } };
      } catch (e) { /* ignore */ }
    };
    connect();

    return () => {
      stop = true;
      clearInterval(pt);
      clearTimeout(reconnectT);
      if (wsRef.current) { try { wsRef.current.close(); } catch (e) { /* ignore */ } }
    };
  }, [user, navigate]);

  return null;
}
